import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatDate, useNavigateToLogout } from "../../common/Common";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Category } from "@mui/icons-material";
import { DDLDataTable } from "../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { IWSIBAssociation } from "../../models/WSIBAssociation";
import { WSIBAssociationInfoSetupFieldsPanel } from "./Panels/WSIBAssociationInfoSetupFieldsPanel";
import { WSIBAssociationService } from "../../services/WSIBAssociationService";
import { WSIBAssociationSetupToolbar } from "./WSIBAssociationSetupToolbar";
import { WSIBAssociationSetupValidationSchema } from "./validator";
import { WSIBCategoryService } from "../../services/WSIBCategoryService";
import { WSIBClassService } from "../../services/WSIBClassService";
import { WSIBCodeService } from "../../services/WSIBCodeService";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { yupResolver } from "@hookform/resolvers/yup";

// import { SingleColumnLayout } from "../EmployeeAssignemnt/SingleColumnLayout";




export interface IWSIBAssociationSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "wsibAssociation-single-data-record";
const WSIB_ASSOCIATION_LIST_DATA_QUERY_KEY = "wsibAssociation-list";

const useGetData = (
	clientId: string,
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id, navigateToLogout],
		queryFn: () => {
			return WSIBAssociationService.getById(clientId, id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const useWSIBAssociation = (
	runGetListAPICall: boolean,
	provinceId: any,
	classLabel: any,
	codeLabel: any,
	categoryLabel: any,
	effectiveDate: any,
	endDate: any,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [WSIB_ASSOCIATION_LIST_DATA_QUERY_KEY, params],
		queryFn: (data) => {

			return WSIBAssociationService.getWSIBAssociation(
				runGetListAPICall,
				provinceId,
				classLabel,
				codeLabel,
				categoryLabel,

				effectiveDate,
				endDate,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});


const useWSIBClassSetupDDL = (
	provinceId: string
) =>
	useQuery({
		queryKey: ["wsib-class-ddl", provinceId],
		queryFn: (data) => {

			return WSIBClassService.getGetWSIBClassDDLByProvinceId(provinceId);
		},
		refetchOnWindowFocus: false,
	});

const useWSIBCategorySetupDDL = (
	provinceId: string
) =>
	useQuery({
		queryKey: ["wsib-category-ddl", provinceId],
		queryFn: (data) => {

			return WSIBCategoryService.getGetWSIBCategoryDDLByProvinceId(provinceId);
		},
		refetchOnWindowFocus: false,
	});

const useWSIBCodeSetupDDL = (
	provinceId: string
) =>
	useQuery({
		queryKey: ["wsib-code-ddl", provinceId],
		queryFn: (data) => {

			return WSIBCodeService.getGetWSIBCodeDDLByProvinceId(provinceId);
		},
		refetchOnWindowFocus: false,
	});

export const WSIBAssociationSetup: React.FC<IWSIBAssociationSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [wsibAssociationId, setWSIBAssociationId] = useState<number | string>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const [isLoading, setIsLoading] = useState(false);
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);
	const [provinceId, setProvinceId] = useState<any>();

	// const [runGetListAPICall, setRunGetListAPICall] =
	// 	useState<any>(true);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "provinceId",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "provinceId",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		// setRunGetListAPICall(true)
	}, []);


	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		clientId,
		editRecordId,
		navigateToLogout
	);

	const { isPending: isLoadingWsibCode, data: wsibCodeSetupDDL } = useWSIBCodeSetupDDL(provinceId);
	const { isPending: isLoadingWsibCategory, data: wsibCategorySetupDDL } = useWSIBCategorySetupDDL(provinceId);
	const { isPending: isLoadingWsibClass, data: wsibClassSetupDDL } = useWSIBClassSetupDDL(provinceId);


	const DEFAULT_VALUES = {
		provinceId: undefined,
		classLabel: undefined,
		categoryLabel: undefined,
		codeLabel: undefined,
		effectiveDate: null,
		endDate: null,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(WSIBAssociationSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});


	const { isPending, data } = useWSIBAssociation(
		true,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined,
		queryParams,
		navigateToLogout
	);

	const wsibAssociationList: any | null = data;


	const { isPending: isSubmitting, mutate: submitWSIBAssociationFormFields } =
		useMutation({
			mutationFn: WSIBAssociationService.create,
			onSuccess: (res: IWSIBAssociation | IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIWSIBAssociationResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/workplace-safety/association/setup/${res.data.id}`;
					navigate(path);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [WSIB_ASSOCIATION_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else if (res.status === 201) {
					// if (instanceOfIWSIBAssociationResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					let path = `/workplace-safety/association/setup/${res.data.id}`;
					navigate(path);
					setEditRecordId(res.data.id);
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [WSIB_ASSOCIATION_LIST_DATA_QUERY_KEY],
						}),
						queryClient.invalidateQueries({
							queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
						}),
					]);
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});




	const upadtedWSIBAssociationListItems = useMemo(() => {
		return (
			wsibAssociationList &&
			wsibAssociationList.items &&
			wsibAssociationList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/workplace-safety/association/setup/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.province}
					</Link>
				),
				code: (item.code),
				class: (item.class),
				Category: (item.category)
			}))
		);
	}, [wsibAssociationList]);



	const onSubmit = (values: any) => {

		let payload = {
			id: id && id,
			navigateToLogout: navigateToLogout,
			provinceId: values.provinceId,
			classLabel: values.classLabel,
			categoryLabel: values.categoryLabel,
			codeLabel: values.codeLabel,
			effectiveDate: formatDate(values.effectiveDate),
			endDate: formatDate(values.endDate),
		};
		submitWSIBAssociationFormFields(payload);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {
			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "effectiveDate" && fieldValue) {
					let date = new Date(fieldValue)
					formMethods.setValue(
						fieldName,
						date
					);
				}
				if (fieldName === "endDate" && fieldValue) {
					let date = new Date(fieldValue)
					formMethods.setValue(
						fieldName,
						date
					);
				}
				if (fieldName === "provinceId") {
					setProvinceId(fieldValue);
				}

			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteWSIBAssociationById } = useMutation({
		mutationFn: WSIBAssociationService.delWSIBAssociation,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the WSIBAssociation fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/workplace-safety/association/setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [WSIB_ASSOCIATION_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const deleteWSIBAssociationField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				wsibAssociationId: wsibAssociationId,
				navigateToLogout: navigateToLogout,
			};
			deleteWSIBAssociationById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setWSIBAssociationId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setWSIBAssociationId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveWSIBAssociationById } = useMutation({
		mutationFn: WSIBAssociationService.archiveWSIBAssociation,

		onSuccess: (res: string | IServerError | IResponse) => {
			if (typeof res === "string") {
				// remove the cache of the WSIBAssociation fields since we just added a new field.
				setSnackbarData({
					open: true,
					message: "Success",
					severity: "success",
				});
			} else {
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			}
			formMethods.reset();
			let path = `/workplace-safety/association/setup`;
			navigate(path);
			return queryClient.invalidateQueries({
				queryKey: [WSIB_ASSOCIATION_LIST_DATA_QUERY_KEY],
			});
		},
		onError: () => {
			// TODO: customize the toast message based on the response
			setSnackbarData({
				open: true,
				message: "Something went wrong, try again later!",
				severity: "error",
			});
		},
	});

	const archiveWSIBAssociationField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				wsibAssociationId: wsibAssociationId,
				navigateToLogout: navigateToLogout,
			};
			archiveWSIBAssociationById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setWSIBAssociationId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		formMethods.setValue('provinceId', undefined)
		let path = `/workplace-safety/association/setup`;
		navigate(path);
	};

	const expandedLabels = ["Workplace Safety", "Association"];
	const expandedWSIBAssociationSetupOrSetupItem = "Setup"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "Association" &&
				item.label === expandedWSIBAssociationSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: parentLabel === "Association" && item.label === expandedWSIBAssociationSetupOrSetupItem && "/workplace-safety/association/setup",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		wsibAssociationName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	const onRowEditClicked = useCallback((params: IWSIBAssociation | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/workplace-safety/association/setup/${params.value}`;
		navigate(path);
	}, []);


	const onProvinceSelectionChangeHandler = (e: any) => {
		setProvinceId(e.value)
		formMethods.setValue("classLabel", undefined);
		formMethods.setValue("categoryLabel", undefined);
		formMethods.setValue("codeLabel", undefined);
	}

	return (
		<Grid
		// sx={{
		// 	pb: "5rem",
		// 	"& .MuiAppBar-root": {
		// 		zIndex: 2,
		// 		top: subMenuVisibility ? "154px" : "112px",
		// 	},
		// }}
		>
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive || isLoadingWsibClass || isLoadingWsibCode || isLoadingWsibCategory || isLoading}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteWSIBAssociationField}
				cancelActionHandler={onCloseHandler}
				testId="wsibAssociation-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 3.5, lg: 3.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="WSIB Association"
										testId={`wsib-association`}
										// leftSpacing={1}
										// rightSpacing={1}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<WSIBAssociationInfoSetupFieldsPanel
											wsibClassSetupDDL={wsibClassSetupDDL}
											wsibCategorySetupDDL={wsibCategorySetupDDL}
											wsibCodeSetupDDL={wsibCodeSetupDDL}
											onProvinceSelectionChangeHandler={onProvinceSelectionChangeHandler}
											setIsLoading={(value: boolean) => setIsLoading(value)}
										/>
									</StandardPanel>
								}
								toolbar={
									<WSIBAssociationSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								showFullDiaplay={true}
								toolbarPosition="bottom"
								testId="form-layout"
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 3, lg: 3, xl: 3 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 5.5, lg: 5.5, xl: 5.5 }}
							sx={{ marginTop: "16px" }}
						>
							<DDLDataTable
								rows={
									wsibAssociationList && upadtedWSIBAssociationListItems
										? upadtedWSIBAssociationListItems
										: []
								}
								columns={[
									{ field: "label", headerName: "Province" },
									{ field: "code", headerName: "Code" },
									{ field: "class", headerName: "Class" },
									{ field: "category", headerName: "Category" },
								]}
								showHeaderColumns={true}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								showArchiveIcon={false}
								isLoading={isPending}
								totalRecords={
									wsibAssociationList && wsibAssociationList.total
										? wsibAssociationList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>

			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveWSIBAssociationField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"wsibAssociation-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
